import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Stepper, Step, StepLabel,
  GridLegacy as Grid, FormControl, InputLabel, Select, MenuItem, Divider, Alert,
  Table, TableHead, TableBody, TableRow, TableCell, IconButton, Chip,
  Checkbox, FormControlLabel, CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { onboardingService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const STEPS = [
  'Personal Details',
  'Prior Experience',
  'Educational Details',
  'Previous Company',
  'Documents & Others',
];

const emptyExperience = () => ({ companyName: '', tenure: '', hrContactNumber: '', hrMailId: '', reasonForChange: '' });
const emptyEducation = () => ({ srNo: 1, courseName: '', yearOfPassOut: '', universityName: '', percentageOrMarks: '' });
const emptyCert = () => ({ certificationSpecialization: '', yearOfPassOut: '', instituteName: '', results: '' });

const OnboardingForm: React.FC = () => {
  const { user } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' | 'warning' } | null>(null);
  const [status, setStatus] = useState('Draft');
  const [changeRequestReason, setChangeRequestReason] = useState('');

  // Personal
  const [personal, setPersonal] = useState({
    bloodGroup: '', contactNo: '', alternateNo: '', dateOfBirth: '',
    maritalStatus: '', currentAddress: '', permanentAddress: '',
    fatherName: '', fatherContact: '', motherName: '', motherContact: '',
    emergencyContact: '', panNo: '', spouseName: '', childrenCount: '',
    hobbies: '', previousAchievements: '',
    uanNumber: '', epfNumber: '',
  });

  // Experience
  const [totalYears, setTotalYears] = useState('');
  const [specializedTech, setSpecializedTech] = useState('');
  const [experiences, setExperiences] = useState([emptyExperience()]);

  // Education
  const [educations, setEducations] = useState([emptyEducation()]);
  const [certifications, setCertifications] = useState([emptyCert()]);

  // Previous Company
  const [prevCompany, setPrevCompany] = useState({
    companyName: '', companyAddress: '', landlineNumber: '', employeeCode: '',
    dateEmployedFrom: '', dateEmployedTo: '', designation: '', salaryDetails: '',
    supervisorName: '', reasonForLeaving: '', employmentType: '', references: '', hrEmailId: '',
  });

  // Documents & Others
  const [checklist, setChecklist] = useState({
    offerLetter: false, relievingLetter: false, paySlipBankStatement: false,
    aadharPanCard: false, educationalDocuments: false,
  });
  const [documentUploads, setDocumentUploads] = useState<any[]>([]);
  const [uploadingDocType, setUploadingDocType] = useState<string | null>(null);
  const [onboardingId, setOnboardingId] = useState<number | null>(null);

  useEffect(() => {
    loadExisting();
  }, []);

  const loadExisting = async () => {
    try {
      const data = await onboardingService.getMy();
      if (data) {
        setOnboardingId(data.id);
        setStatus(data.status);
        setChangeRequestReason(data.changeRequestReason || '');
        setPersonal({
          bloodGroup: data.bloodGroup || '', contactNo: data.contactNo || '',
          alternateNo: data.alternateNo || '', dateOfBirth: data.dateOfBirth ? data.dateOfBirth.split('T')[0] : '',
          maritalStatus: data.maritalStatus || '', currentAddress: data.currentAddress || '',
          permanentAddress: data.permanentAddress || '', fatherName: data.fatherName || '',
          fatherContact: data.fatherContact || '', motherName: data.motherName || '',
          motherContact: data.motherContact || '', emergencyContact: data.emergencyContact || '',
          panNo: data.panNo || '', spouseName: data.spouseName || '',
          childrenCount: data.childrenCount?.toString() || '', hobbies: data.hobbies || '',
          previousAchievements: data.previousAchievements || '',
          uanNumber: data.uanNumber || '', epfNumber: data.epfNumber || '',
        });
        setTotalYears(data.totalYearsExperience || '');
        setSpecializedTech(data.specializedTechnology || '');
        if (data.experiences?.length) setExperiences(data.experiences.map((e: any) => ({
          companyName: e.companyName || '', tenure: e.tenure || '',
          hrContactNumber: e.hrContactNumber || '', hrMailId: e.hrMailId || '',
          reasonForChange: e.reasonForChange || '',
        })));
        if (data.educations?.length) setEducations(data.educations.map((e: any) => ({
          srNo: e.srNo, courseName: e.courseName || '', yearOfPassOut: e.yearOfPassOut || '',
          universityName: e.universityName || '', percentageOrMarks: e.percentageOrMarks || '',
        })));
        if (data.certifications?.length) setCertifications(data.certifications.map((c: any) => ({
          certificationSpecialization: c.certificationSpecialization || '',
          yearOfPassOut: c.yearOfPassOut || '', instituteName: c.instituteName || '', results: c.results || '',
        })));
        if (data.previousCompany) setPrevCompany({
          companyName: data.previousCompany.companyName || '',
          companyAddress: data.previousCompany.companyAddress || '',
          landlineNumber: data.previousCompany.landlineNumber || '',
          employeeCode: data.previousCompany.employeeCode || '',
          dateEmployedFrom: data.previousCompany.dateEmployedFrom || '',
          dateEmployedTo: data.previousCompany.dateEmployedTo || '',
          designation: data.previousCompany.designation || '',
          salaryDetails: data.previousCompany.salaryDetails || '',
          supervisorName: data.previousCompany.supervisorName || '',
          reasonForLeaving: data.previousCompany.reasonForLeaving || '',
          employmentType: data.previousCompany.employmentType || '',
          references: data.previousCompany.references || '',
          hrEmailId: data.previousCompany.hrEmailId || '',
        });
        if (data.documentChecklist) setChecklist({
          offerLetter: data.documentChecklist.offerLetter,
          relievingLetter: data.documentChecklist.relievingLetter,
          paySlipBankStatement: data.documentChecklist.paySlipBankStatement,
          aadharPanCard: data.documentChecklist.aadharPanCard,
          educationalDocuments: data.documentChecklist.educationalDocuments,
        });
        if (data.documentUploads) setDocumentUploads(data.documentUploads);
      }
    } catch { }
    setLoading(false);
  };

  const isLocked = status === 'Submitted' || status === 'Approved';

  const handleDocumentUpload = (docType: string) => async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !onboardingId) return;
    setUploadingDocType(docType);
    for (const file of Array.from(files)) {
      try {
        const result = await onboardingService.uploadDocument(onboardingId, docType, file);
        setDocumentUploads(prev => [...prev, result]);
      } catch { }
    }
    setUploadingDocType(null);
    e.target.value = '';
  };

  const handleDocumentDelete = async (docId: number) => {
    if (!onboardingId) return;
    try {
      await onboardingService.deleteDocument(onboardingId, docId);
      setDocumentUploads(prev => prev.filter((d: any) => d.id !== docId));
    } catch { }
  };

  const validateForSubmit = (): string | null => {
    const missing: string[] = [];
    if (!personal.contactNo.trim()) missing.push('Contact No.');
    if (!personal.dateOfBirth) missing.push('Date of Birth');
    if (!personal.currentAddress.trim()) missing.push('Current Address');
    if (!personal.emergencyContact.trim()) missing.push('Emergency Contact');
    if (!personal.panNo.trim()) missing.push('PAN No.');
    if (missing.length) return `Please fill in required fields: ${missing.join(', ')}`;
    return null;
  };

  const buildPayload = () => ({
    ...personal,
    dateOfBirth: personal.dateOfBirth || null,
    childrenCount: personal.childrenCount ? parseInt(personal.childrenCount) : null,
    totalYearsExperience: totalYears,
    specializedTechnology: specializedTech,
    experiences: experiences.filter(e => e.companyName.trim()),
    educations: educations.filter(e => e.courseName.trim()).map((e, i) => ({ ...e, srNo: i + 1 })),
    certifications: certifications.filter(c => c.certificationSpecialization.trim()),
    previousCompany: prevCompany,
    documentChecklist: checklist,
  });

  const handleSave = async () => {
    if (isLocked) return;
    setSaving(true);
    try {
      await onboardingService.save(buildPayload());
      setStatus('Draft');
      setMessage({ text: 'Progress saved successfully.', type: 'success' });
    } catch (e: any) {
      setMessage({ text: e.response?.data?.message || 'Save failed.', type: 'error' });
    }
    setSaving(false);
  };

  const handleSubmit = async () => {
    const validationError = validateForSubmit();
    if (validationError) {
      setMessage({ text: validationError, type: 'error' });
      setActiveStep(0);
      return;
    }
    setSaving(true);
    try {
      await onboardingService.save(buildPayload());
      await onboardingService.submit();
      setStatus('Submitted');
      setMessage({ text: 'Form submitted! HR will review it shortly.', type: 'success' });
    } catch (e: any) {
      setMessage({ text: e.response?.data?.message || 'Submit failed.', type: 'error' });
    }
    setSaving(false);
  };

  const statusColor: any = { Draft: 'default', Submitted: 'primary', Approved: 'success', ChangesRequested: 'warning' };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
        <Typography variant="h5" fontWeight={600}>Employee Onboarding Form</Typography>
        <Chip label={status} color={statusColor[status] || 'default'} />
      </Box>

      {status === 'ChangesRequested' && changeRequestReason && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>HR requested changes:</strong> {changeRequestReason}
        </Alert>
      )}
      {status === 'Approved' && (
        <Alert severity="success" sx={{ mb: 2 }}>Your onboarding form has been approved.</Alert>
      )}
      {status === 'Submitted' && (
        <Alert severity="info" sx={{ mb: 2 }}>Form submitted. Awaiting HR review. You cannot edit until HR responds.</Alert>
      )}
      {message && <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}

      <Stepper activeStep={activeStep} sx={{ mb: 3 }} alternativeLabel>
        {STEPS.map(label => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
      </Stepper>

      <Paper sx={{ p: 3 }}>
        {/* STEP 0 — Personal Details */}
        {activeStep === 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>Personal Details</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Full Name (from account)" value={user?.fullName || ''} disabled size="small" helperText="Set in your profile" /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Blood Group" value={personal.bloodGroup} onChange={e => setPersonal({ ...personal, bloodGroup: e.target.value })} size="small" disabled={isLocked} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth required label="Contact No." value={personal.contactNo} onChange={e => setPersonal({ ...personal, contactNo: e.target.value })} size="small" disabled={isLocked} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Alternate No." value={personal.alternateNo} onChange={e => setPersonal({ ...personal, alternateNo: e.target.value })} size="small" disabled={isLocked} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth required label="Date of Birth" type="date" value={personal.dateOfBirth} onChange={e => setPersonal({ ...personal, dateOfBirth: e.target.value })} size="small" InputLabelProps={{ shrink: true }} disabled={isLocked} /></Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" disabled={isLocked}>
                  <InputLabel>Marital Status</InputLabel>
                  <Select value={personal.maritalStatus} onChange={e => setPersonal({ ...personal, maritalStatus: e.target.value })} label="Marital Status">
                    <MenuItem value="Single">Single</MenuItem>
                    <MenuItem value="Married">Married</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}><TextField fullWidth required label="Current Address" value={personal.currentAddress} onChange={e => setPersonal({ ...personal, currentAddress: e.target.value })} size="small" multiline rows={2} disabled={isLocked} /></Grid>
              <Grid item xs={12}><TextField fullWidth label="Permanent Address" value={personal.permanentAddress} onChange={e => setPersonal({ ...personal, permanentAddress: e.target.value })} size="small" multiline rows={2} disabled={isLocked} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Father's Name" value={personal.fatherName} onChange={e => setPersonal({ ...personal, fatherName: e.target.value })} size="small" disabled={isLocked} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Father's Contact" value={personal.fatherContact} onChange={e => setPersonal({ ...personal, fatherContact: e.target.value })} size="small" disabled={isLocked} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Mother's Name" value={personal.motherName} onChange={e => setPersonal({ ...personal, motherName: e.target.value })} size="small" disabled={isLocked} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Mother's Contact" value={personal.motherContact} onChange={e => setPersonal({ ...personal, motherContact: e.target.value })} size="small" disabled={isLocked} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth required label="Emergency Contact" value={personal.emergencyContact} onChange={e => setPersonal({ ...personal, emergencyContact: e.target.value })} size="small" disabled={isLocked} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth required label="PAN No." value={personal.panNo} onChange={e => setPersonal({ ...personal, panNo: e.target.value })} size="small" disabled={isLocked} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Spouse Name" value={personal.spouseName} onChange={e => setPersonal({ ...personal, spouseName: e.target.value })} size="small" disabled={isLocked} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="No. of Children" type="number" value={personal.childrenCount} onChange={e => setPersonal({ ...personal, childrenCount: e.target.value })} size="small" inputProps={{ min: 0 }} disabled={isLocked} /></Grid>
              <Grid item xs={12}><TextField fullWidth label="Hobbies" value={personal.hobbies} onChange={e => setPersonal({ ...personal, hobbies: e.target.value })} size="small" disabled={isLocked} /></Grid>
              <Grid item xs={12}><TextField fullWidth label="Previous Achievements" value={personal.previousAchievements} onChange={e => setPersonal({ ...personal, previousAchievements: e.target.value })} size="small" multiline rows={2} disabled={isLocked} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="UAN Number (EPF)" value={personal.uanNumber} onChange={e => setPersonal({ ...personal, uanNumber: e.target.value })} size="small" disabled={isLocked} helperText="12-digit Universal Account Number" /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="EPF / PF Member ID" value={personal.epfNumber} onChange={e => setPersonal({ ...personal, epfNumber: e.target.value })} size="small" disabled={isLocked} helperText="From previous employer (if applicable)" /></Grid>
            </Grid>
          </Box>
        )}

        {/* STEP 1 — Prior Experience */}
        {activeStep === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>Prior Experience</Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Total Years of Experience" value={totalYears} onChange={e => setTotalYears(e.target.value)} size="small" disabled={isLocked} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Specialized Technology" value={specializedTech} onChange={e => setSpecializedTech(e.target.value)} size="small" disabled={isLocked} /></Grid>
            </Grid>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell>Company Name</TableCell><TableCell>Tenure</TableCell>
                  <TableCell>HR Contact</TableCell><TableCell>HR Email</TableCell>
                  <TableCell>Reason for Change</TableCell>
                  {!isLocked && <TableCell />}
                </TableRow>
              </TableHead>
              <TableBody>
                {experiences.map((exp, i) => (
                  <TableRow key={i}>
                    <TableCell><TextField value={exp.companyName} onChange={e => { const r = [...experiences]; r[i].companyName = e.target.value; setExperiences(r); }} size="small" fullWidth disabled={isLocked} /></TableCell>
                    <TableCell><TextField value={exp.tenure} onChange={e => { const r = [...experiences]; r[i].tenure = e.target.value; setExperiences(r); }} size="small" fullWidth disabled={isLocked} /></TableCell>
                    <TableCell><TextField value={exp.hrContactNumber} onChange={e => { const r = [...experiences]; r[i].hrContactNumber = e.target.value; setExperiences(r); }} size="small" fullWidth disabled={isLocked} /></TableCell>
                    <TableCell><TextField value={exp.hrMailId} onChange={e => { const r = [...experiences]; r[i].hrMailId = e.target.value; setExperiences(r); }} size="small" fullWidth disabled={isLocked} /></TableCell>
                    <TableCell><TextField value={exp.reasonForChange} onChange={e => { const r = [...experiences]; r[i].reasonForChange = e.target.value; setExperiences(r); }} size="small" fullWidth disabled={isLocked} /></TableCell>
                    {!isLocked && <TableCell><IconButton size="small" color="error" onClick={() => setExperiences(experiences.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton></TableCell>}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {!isLocked && <Button startIcon={<AddIcon />} onClick={() => setExperiences([...experiences, emptyExperience()])} sx={{ mt: 1 }} size="small">Add Row</Button>}
          </Box>
        )}

        {/* STEP 2 — Educational Details */}
        {activeStep === 2 && (
          <Box>
            <Typography variant="h6" gutterBottom>Educational Details</Typography>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell>Sr.</TableCell><TableCell>Course Name (10th to Highest)</TableCell>
                  <TableCell>Year of Pass Out</TableCell><TableCell>University / Institute</TableCell>
                  <TableCell>% / Marks</TableCell>
                  {!isLocked && <TableCell />}
                </TableRow>
              </TableHead>
              <TableBody>
                {educations.map((edu, i) => (
                  <TableRow key={i}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell><TextField value={edu.courseName} onChange={e => { const r = [...educations]; r[i].courseName = e.target.value; setEducations(r); }} size="small" fullWidth disabled={isLocked} /></TableCell>
                    <TableCell><TextField value={edu.yearOfPassOut} onChange={e => { const r = [...educations]; r[i].yearOfPassOut = e.target.value; setEducations(r); }} size="small" fullWidth disabled={isLocked} /></TableCell>
                    <TableCell><TextField value={edu.universityName} onChange={e => { const r = [...educations]; r[i].universityName = e.target.value; setEducations(r); }} size="small" fullWidth disabled={isLocked} /></TableCell>
                    <TableCell><TextField value={edu.percentageOrMarks} onChange={e => { const r = [...educations]; r[i].percentageOrMarks = e.target.value; setEducations(r); }} size="small" fullWidth disabled={isLocked} /></TableCell>
                    {!isLocked && <TableCell><IconButton size="small" color="error" onClick={() => setEducations(educations.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton></TableCell>}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {!isLocked && <Button startIcon={<AddIcon />} onClick={() => setEducations([...educations, emptyEducation()])} sx={{ mt: 1 }} size="small">Add Row</Button>}

            <Divider sx={{ my: 3 }} />
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Certifications</Typography>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell>Specialization</TableCell><TableCell>Year</TableCell>
                  <TableCell>Institute</TableCell><TableCell>Results</TableCell>
                  {!isLocked && <TableCell />}
                </TableRow>
              </TableHead>
              <TableBody>
                {certifications.map((cert, i) => (
                  <TableRow key={i}>
                    <TableCell><TextField value={cert.certificationSpecialization} onChange={e => { const r = [...certifications]; r[i].certificationSpecialization = e.target.value; setCertifications(r); }} size="small" fullWidth disabled={isLocked} /></TableCell>
                    <TableCell><TextField value={cert.yearOfPassOut} onChange={e => { const r = [...certifications]; r[i].yearOfPassOut = e.target.value; setCertifications(r); }} size="small" fullWidth disabled={isLocked} /></TableCell>
                    <TableCell><TextField value={cert.instituteName} onChange={e => { const r = [...certifications]; r[i].instituteName = e.target.value; setCertifications(r); }} size="small" fullWidth disabled={isLocked} /></TableCell>
                    <TableCell><TextField value={cert.results} onChange={e => { const r = [...certifications]; r[i].results = e.target.value; setCertifications(r); }} size="small" fullWidth disabled={isLocked} /></TableCell>
                    {!isLocked && <TableCell><IconButton size="small" color="error" onClick={() => setCertifications(certifications.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton></TableCell>}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {!isLocked && <Button startIcon={<AddIcon />} onClick={() => setCertifications([...certifications, emptyCert()])} sx={{ mt: 1 }} size="small">Add Row</Button>}
          </Box>
        )}

        {/* STEP 3 — Previous Company */}
        {activeStep === 3 && (
          <Box>
            <Typography variant="h6" gutterBottom>Previous Company Details</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Company Name" value={prevCompany.companyName} onChange={e => setPrevCompany({ ...prevCompany, companyName: e.target.value })} size="small" disabled={isLocked} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Employee Code" value={prevCompany.employeeCode} onChange={e => setPrevCompany({ ...prevCompany, employeeCode: e.target.value })} size="small" disabled={isLocked} /></Grid>
              <Grid item xs={12}><TextField fullWidth label="Company Address" value={prevCompany.companyAddress} onChange={e => setPrevCompany({ ...prevCompany, companyAddress: e.target.value })} size="small" disabled={isLocked} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Company Landline" value={prevCompany.landlineNumber} onChange={e => setPrevCompany({ ...prevCompany, landlineNumber: e.target.value })} size="small" disabled={isLocked} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="HR Email ID" value={prevCompany.hrEmailId} onChange={e => setPrevCompany({ ...prevCompany, hrEmailId: e.target.value })} size="small" disabled={isLocked} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Date Employed From (MM/DD/YY)" value={prevCompany.dateEmployedFrom} onChange={e => setPrevCompany({ ...prevCompany, dateEmployedFrom: e.target.value })} size="small" disabled={isLocked} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Date Employed To (MM/DD/YY)" value={prevCompany.dateEmployedTo} onChange={e => setPrevCompany({ ...prevCompany, dateEmployedTo: e.target.value })} size="small" disabled={isLocked} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Designation" value={prevCompany.designation} onChange={e => setPrevCompany({ ...prevCompany, designation: e.target.value })} size="small" disabled={isLocked} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Salary Details (per month/annum)" value={prevCompany.salaryDetails} onChange={e => setPrevCompany({ ...prevCompany, salaryDetails: e.target.value })} size="small" disabled={isLocked} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Supervisor Name & Designation" value={prevCompany.supervisorName} onChange={e => setPrevCompany({ ...prevCompany, supervisorName: e.target.value })} size="small" disabled={isLocked} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Reason for Leaving" value={prevCompany.reasonForLeaving} onChange={e => setPrevCompany({ ...prevCompany, reasonForLeaving: e.target.value })} size="small" disabled={isLocked} /></Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" disabled={isLocked}>
                  <InputLabel>Employment Type</InputLabel>
                  <Select value={prevCompany.employmentType} onChange={e => setPrevCompany({ ...prevCompany, employmentType: e.target.value })} label="Employment Type">
                    <MenuItem value="Permanent">Permanent</MenuItem>
                    <MenuItem value="Contractual">Contractual</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}><TextField fullWidth label="Two References (Names & Contacts)" value={prevCompany.references} onChange={e => setPrevCompany({ ...prevCompany, references: e.target.value })} size="small" multiline rows={2} disabled={isLocked} /></Grid>
            </Grid>
          </Box>
        )}

        {/* STEP 4 — Documents & Others */}
        {activeStep === 4 && (
          <Box>
            <Typography variant="h6" gutterBottom>Documents Submitted</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Check the documents you have submitted from your previous company:</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[
                ['offerLetter', 'Offer Letter'],
                ['relievingLetter', 'Relieving Letter'],
                ['paySlipBankStatement', 'Pay Slip / Bank Statements'],
                ['aadharPanCard', 'Aadhar & PAN Card Photocopy'],
                ['educationalDocuments', 'Educational Documents'],
              ].map(([key, label]) => (
                <FormControlLabel key={key} control={
                  <Checkbox checked={(checklist as any)[key]} onChange={e => setChecklist({ ...checklist, [key]: e.target.checked })} disabled={isLocked} />
                } label={label} />
              ))}
            </Box>

            <Divider sx={{ my: 3 }} />
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Upload Documents</Typography>
            {!onboardingId && (
              <Typography variant="caption" color="warning.main">Save your progress first to enable uploads.</Typography>
            )}

            {[
              { type: 'ExperienceLetter',   label: 'Experience Letter',           hint: 'From previous employer' },
              { type: 'AppointmentLetter',  label: 'Appointment Letter',          hint: 'Offer / appointment letter from previous employer' },
              { type: 'SalarySlip',         label: 'Salary Slips (Last 3 Months)', hint: 'PDF or image of recent 3 salary slips' },
              { type: 'Educational',        label: 'Educational Documents',       hint: 'Degree certificates, marksheets' },
              { type: 'Other',              label: 'Other Documents',             hint: 'Any other supporting documents' },
            ].map(({ type, label, hint }) => {
              const files = documentUploads.filter((d: any) => d.documentType === type);
              const uploading = uploadingDocType === type;
              return (
                <Box key={type} sx={{ mb: 2, p: 2, border: '1px solid #e8e8e8', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{label}</Typography>
                      <Typography variant="caption" color="text.secondary">{hint}</Typography>
                    </Box>
                    {!isLocked && (
                      <Button size="small" variant="outlined" component="label" disabled={uploading || !onboardingId}>
                        {uploading ? 'Uploading...' : '+ Add Files'}
                        <input type="file" hidden multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={handleDocumentUpload(type)} />
                      </Button>
                    )}
                  </Box>
                  {files.length === 0 && <Typography variant="caption" color="text.secondary">No files uploaded.</Typography>}
                  {files.map((doc: any) => (
                    <Box key={doc.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        <a href={doc.url} target="_blank" rel="noopener noreferrer">{doc.fileName}</a>
                      </Typography>
                      {!isLocked && (
                        <IconButton size="small" color="error" onClick={() => handleDocumentDelete(doc.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                </Box>
              );
            })}
          </Box>
        )}

        {/* Navigation */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button disabled={activeStep === 0} onClick={() => setActiveStep(s => s - 1)}>Back</Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!isLocked && <Button variant="outlined" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Progress'}</Button>}
            {activeStep < STEPS.length - 1
              ? <Button variant="contained" onClick={() => setActiveStep(s => s + 1)}>Next</Button>
              : !isLocked && <Button variant="contained" color="success" onClick={handleSubmit} disabled={saving}>{saving ? 'Submitting...' : 'Submit Form'}</Button>
            }
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default OnboardingForm;
